import { getLabels, levelKeyMap } from '@components/Charts/AssessmentVIFCharts'
import { Row } from '@tanstack/react-table'
import {
  commonActionColumn,
  renderGradeColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { formatReportDate } from '@utils/dateUtils'
import getFormattedDurationFromSecond from '@utils/getFormatedDurationFromSecond'
import { Progress } from 'antd'

import {
  Database_Procs_TeamVIF_Item,
  Database_Procs_VIFAggregatedByLevel_Item
} from '@suntex.fim/api-client'
import { getVifGames, vifHighlight } from '@suntex.fim/assessment'
import { $ArithmeticOperation } from '@suntex.fim/enums'
import { ColumnType, Tooltip } from '@suntex.fim/ui'

import {
  generateCommonColumns,
  generateCommonColumnsForSchool
} from './CommonColumns'

export type VIFReportData = Partial<
  | (Database_Procs_VIFAggregatedByLevel_Item &
      Partial<Database_Procs_TeamVIF_Item>)
  | Database_Procs_TeamVIF_Item
>

export const allColumnKey = $ArithmeticOperation
  .describeAll()
  .map((m) => m.slug)
/**
 * handles hidable columns list
 */
export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  add: 'Addition',
  sub: 'Subtraction',
  mul: 'Multiplication',
  div: 'Division'
}

const getDataSetKeys = (label: string) => {
  const filteredDataSetkeys =
    label === 'Addition' || label === 'Subtraction'
      ? ['64 Facts', '100 Facts', '10k Facts']
      : ['36 Facts', '100 Facts', '10k Facts']
  return filteredDataSetkeys
}
export const schoolHidableSelectLabel: Record<string, string> = {
  name: 'Name',
  desc: 'Class Name',
  contactName: 'Teacher',
  grade: 'Grade',
  students: 'Students',
  stickers: 'Stickers',
  Addition: 'Addition',
  Subtraction: 'Subtraction',
  Multiplication: 'Multiplication',
  Division: 'Division'
}
export const getVIFColumnKeys = (level: number) => {
  // Initialize the columnKeys
  let columnKeys = [...allColumnKey]
  if (level === 0) {
    columnKeys = [...allColumnKey].splice(0, 2)
  } else if (level === 1) {
    columnKeys = [...allColumnKey].splice(2, 2)
  } else {
    columnKeys = [...allColumnKey]
  }
  return columnKeys
}
export const schoolExportColumn = (level: number) => {
  const result: Record<string, string> = schoolHidableSelectLabel
  const labels = getLabels(level)
  labels.forEach((label: string) => {
    const filteredDataSetkeys = getDataSetKeys(label)

    filteredDataSetkeys.forEach((subKey) => {
      const key = `${levelKeyMap[subKey][label]}_completed_count`
      const value = `${label} ${subKey}`
      result[key] = value
    })
  })
  return result
}

/**
 * returns JSX for very important facts report table
 */
function columns({
  level,
  graphColor,
  sendMessage
}: {
  sendMessage: (t: Row<VIFReportData>[]) => void
  level: number
  graphColor: string
}) {
  const columnKeys = getVIFColumnKeys(level)
  /**
   * renders progress bar in table cell
   */
  const levelColumns: ColumnType<VIFReportData>[] = columnKeys.map((key) => ({
    header: hidableSelectLabel[key],
    accessorFn: (infoValue) =>
      Object.entries(getVifGames($ArithmeticOperation.fromSlug(key))).map(
        ([gameKey, value]) => [
          value,
          Number(infoValue[gameKey as keyof VIFReportData]) || 0
        ]
      ),
    enableSorting: false,
    id: key,
    cell: (info) => {
      const infoValue = info.row.original
      const entries = Object.entries(
        getVifGames($ArithmeticOperation.fromSlug(key))
      )
      return (
        <div
          style={{
            overflow: 'hidden',
            display: 'grid',
            gridGap: '2px',
            gridTemplateColumns: `repeat(${entries.length}, minmax(12px, 1fr))`
          }}
        >
          {entries.map(([newKey, gameName]) => {
            const isHighlightGames = vifHighlight.includes(newKey)

            const dateAndTime = infoValue[`${newKey}_date`] as string
            const value = Number(infoValue[newKey as keyof VIFReportData]) || 0
            return (
              <Tooltip
                classNames={{ root: 'progress-tooltip' }}
                placement="right"
                arrow={false}
                key={newKey}
                title={
                  <div>
                    <div>
                      {gameName} is {value}% completed.
                    </div>
                    {value > 0 ? (
                      <>
                        <div>{`This game was last played by ${info.row.original.userId} on ${formatReportDate(new Date(dateAndTime || ''))}.`}</div>
                        <div>
                          User spent{' '}
                          {getFormattedDurationFromSecond(
                            Number(
                              infoValue[`${newKey}_time` as keyof VIFReportData]
                            )
                          )}{' '}
                          on this Task.
                        </div>
                      </>
                    ) : (
                      <div>Not Yet Played by {info.row.original.userId}.</div>
                    )}
                  </div>
                }
              >
                <Progress
                  trailColor={isHighlightGames ? '#333333b3' : '#1d1d1d4d'}
                  strokeColor={isHighlightGames ? '#5645DA' : graphColor}
                  percent={value}
                  showInfo={false}
                  size={['100%', 23]}
                  className="custom-step-progress"
                />
              </Tooltip>
            )
          })}
        </div>
      )
    }
  }))

  const actioncolumn = commonActionColumn<VIFReportData>(
    hidableSelectLabel,
    sendMessage
  )

  /**
   * handles table column mapping
   */
  const finalColumns: ColumnType<VIFReportData>[] = [
    actioncolumn,
    ...generateCommonColumns<VIFReportData>(),
    ...levelColumns
  ]
  return finalColumns
}
function schoolColumns({
  level,
  graphColor
}: {
  level: number
  graphColor: string
}) {
  const labels = getLabels(level)
  /**
   * renders progress bar in table cell
   */
  const levelColumns: ColumnType<VIFReportData>[] = labels.map((label: any) => {
    // Conditionally filter dataSetkeys based on label
    const filteredDataSetkeys = getDataSetKeys(label)

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      header: label,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      id: label,
      columns: filteredDataSetkeys.map((subKey) => {
        const id = `${levelKeyMap[subKey][label]}_completed_count`
        return {
          header: renderHeaderTooltip(`${subKey}`),
          accessorKey: `${levelKeyMap[subKey][label]}_completed_count`,
          id,
          size: 50,
          cell: (info: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const infoValue = info?.row.original
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const students = info?.row.original?.students
            const calculateValue =
              (infoValue[`${levelKeyMap[subKey][label]}_completed_count`] /
                students) *
              100
            const entries = +calculateValue.toFixed(1) || 0
            return (
              <div
                style={{
                  overflow: 'hidden',
                  display: 'grid',
                  gridGap: '2px',
                  gridTemplateColumns: `repeat(${(entries as unknown as string).length}, minmax(12px, 1fr))`
                }}
              >
                <Tooltip
                  classNames={{ root: 'progress-tooltip' }}
                  placement="right"
                  arrow={false}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  key={label}
                  title={
                    <div>
                      <div>{entries}% completed.</div>
                    </div>
                  }
                >
                  <Progress
                    trailColor=""
                    strokeColor={graphColor}
                    percent={entries}
                    showInfo={false}
                    size={['100%', 23]}
                    className="custom-step-progress"
                  />
                </Tooltip>
              </div>
            )
          }
        }
      })
    }
  })
  const schoolCommonColumns = generateCommonColumnsForSchool<VIFReportData>()
  /**
   * handles table column mapping
   */
  const finalColumns: ColumnType<VIFReportData>[] = [
    ...schoolCommonColumns.slice(0, 3),
    {
      header: () => renderHeaderTooltip('Grade'),
      size: 50,
      accessorKey: 'grade',
      cell: (info) => {
        return renderGradeColumn(info.getValue())
      },
      id: 'grade',
      enableSorting: true
    },
    ...schoolCommonColumns.slice(4),
    ...levelColumns
  ]
  return finalColumns
}

export { columns, schoolColumns }
